import { addDoc, collection, doc, setDoc } from "firebase/firestore";
import { makeObservable } from "mobx";
import { DbInfoType } from "../dev/DevsStore";
import { log } from "../ui/log";
import type { Api } from "./apis";
import type { Stores } from "./stores";
import { trackEvent } from "./trackEvent";

export default abstract class BaseWriteApi<ModelData> {
  private createCount = 0;
  private addCount = 0;
  private setCount = 0;

  private resetCounters() {
    this.createCount = this.addCount = this.setCount = 0;
  }

  private setTick() {
    this.setCount++;
    this.checkTicks();
  }

  private addTick() {
    this.addCount++;
    this.checkTicks();
  }

  private createTick() {
    this.createCount++;
    this.checkTicks();
  }

  private checkTicks() {
    if (this.createCount + this.addCount + this.setCount > 50) {
      this.stores.devsStore.gotError = new Error(
        `DB_WRITE_USAGE_ERROR_${this.addCount}_${this.setCount}__${this.db}`,
      );
    }
  }

  constructor(
    protected api: Api,
    protected stores: Stores,
  ) {
    makeObservable(this);

    setInterval(() => {
      this.resetCounters();
    }, 1000);
  }

  get dbPrefix() {
    const isStaging = window?.location?.host.includes("--staging");

    return __DEV__ ? "dev_" : isStaging ? "staging_" : "prod_";
  }

  get dbCollection() {
    return collection(this.api.client, this.db);
  }

  async add(item: Omit<ModelData, "uid">): Promise<ModelData> {
    this.addTick();

    if (__DEV__) {
      log.network(`Add db ${this.db} data:`, item);
    }

    const docRef = await addDoc(this.dbCollection, item);

    this.stores.devsStore.addDbStats(DbInfoType.Add, this.db, docRef.id);

    trackEvent(`add_in_${this.db}`);

    return { ...item, uid: docRef.id } as ModelData;
  }

  async create(
    uid: string,
    itemData: Omit<ModelData, "uid">,
  ): Promise<ModelData> {
    this.createTick();

    if (__DEV__) {
      log.network(`Create db ${this.db} data:`, itemData);
    }

    const docRef = doc(this.api.client, this.db, uid);
    await setDoc(docRef, itemData);

    this.stores.devsStore.addDbStats(DbInfoType.Set, this.db, docRef.id);

    trackEvent(`create_in_${this.db}`);

    return { ...itemData, uid: docRef.id } as ModelData;
  }

  abstract get db(): string;
}
